import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "../utils/api";
import { resolveAssetUrl } from "../utils/config";
import { uploadErrorMessage } from "../utils/fileUpload";
import FileUploader, { ExistingFiles } from "../components/FileUploader";
import "../styles/formPage.css";

const PortfolioEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({ title: "", content: "", url: "", type: "" });
  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState(new Set());

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    setError(null);
    API.get(`/portfolio/${id}`)
      .then((res) => {
        if (ignore) return;
        const p = res.data.content;
        setForm({ title: p.title || "", content: p.content || "", url: p.url || "", type: p.type || "" });
        setExistingImages(p.images || []);
      })
      .catch(() => { if (!ignore) setError("포트폴리오를 불러오는데 실패했습니다."); })
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [id]);

  const change = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const toggleDelete = (imageId) => setImagesToDelete((prev) => {
    const next = new Set(prev);
    if (next.has(imageId)) next.delete(imageId);
    else next.add(imageId);
    return next;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return alert("제목을 입력해주세요.");
    if (!form.content.trim()) return alert("내용을 입력해주세요.");
    const kept = existingImages.length - imagesToDelete.size + newImages.length;
    if (kept > 5) return alert("이미지는 최대 5장까지 가능합니다.");

    try {
      setSubmitting(true);
      const update = { title: form.title.trim(), content: form.content.trim() };
      if (form.url.trim()) update.url = form.url.trim();
      if (form.type.trim()) update.type = form.type.trim();
      const keepImageIds = existingImages.filter((img) => !imagesToDelete.has(img.id)).map((img) => img.id);

      const fd = new FormData();
      fd.append("update", new Blob([JSON.stringify(update)], { type: "application/json" }));
      newImages.forEach((img) => fd.append("images", img));
      fd.append("keepImageIds", new Blob([JSON.stringify(keepImageIds)], { type: "application/json" }));

      await API.put(`/portfolio/${id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      alert("포트폴리오가 수정되었습니다.");
      navigate(`/portfolio/${id}`);
    } catch (err) {
      alert(uploadErrorMessage(err, "포트폴리오 수정에 실패했습니다."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm("수정을 취소하시겠습니까? 변경사항이 저장되지 않습니다.")) navigate(`/portfolio/${id}`);
  };

  if (loading) {
    return <div className="container section state-block"><div className="spinner" />불러오는 중...</div>;
  }
  if (error) {
    return (
      <div className="container section state-block">
        <p className="state-block__title">{error}</p>
        <button className="btn btn--outline btn--sm" onClick={() => navigate("/portfolio")}>목록으로</button>
      </div>
    );
  }

  return (
    <div className="form-page container container--narrow section">
      <div className="form-page__head">
        <span className="section__eyebrow">Portfolio</span>
        <h1 className="form-page__title">포트폴리오 수정</h1>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="field">
          <label className="field__label" htmlFor="title">제목 <span className="req">*</span></label>
          <input id="title" name="title" className="input" type="text" value={form.title} onChange={change} disabled={submitting} />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="content">내용 <span className="req">*</span></label>
          <textarea id="content" name="content" className="textarea" value={form.content} onChange={change} disabled={submitting} />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="url">관련 URL (선택)</label>
          <input id="url" name="url" className="input" type="url" value={form.url} onChange={change} disabled={submitting} />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="type">분류 (선택)</label>
          <input id="type" name="type" className="input" type="text" value={form.type} onChange={change} disabled={submitting} />
        </div>

        <ExistingFiles
          label="기존 이미지"
          items={existingImages}
          deletedIds={imagesToDelete}
          onToggle={toggleDelete}
          image
          resolveUrl={resolveAssetUrl}
        />

        <FileUploader
          id="new-images"
          label="새 이미지 추가"
          hint="기존 이미지 포함 총 5장까지 가능합니다"
          accept="image/*"
          image
          files={newImages}
          onChange={setNewImages}
          disabled={submitting}
        />

        <div className="form-actions">
          <button type="button" className="btn btn--ghost" onClick={handleCancel} disabled={submitting}>취소</button>
          <button type="submit" className="btn btn--primary" disabled={submitting}>
            {submitting ? "수정 중..." : "수정하기"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PortfolioEdit;
